import React, { useState, useContext, useEffect } from 'react'
import { HeaderProps } from '../index'
import { useHistory } from 'react-router'
import { IdentityContext } from '#src/containers/Identity'
import useBreakpoint from '#src/hooks/useBreakpoint'
import { UserAgentContext } from '#src/containers/UserAgent'
import { NavigationContextProvider } from './NavigationContext'
import {
  DepartmentNavigationTopLevelItemFragment,
  DepartmentNavigationSubSectionFragment,
  DepartmentNavigationTertiaryItemFragment
} from '../NavHierarchyFragments.generated'
import { useDepartmentContext } from '#src/context/department'
import Tablet from './resolutions/Tablet'
import Mobile from './resolutions/Mobile'
import Desktop from './resolutions/Desktop'
import NavigationVeil from './NavigationVeil'
import Cart from './Cart'
import Search from './Search'
import StickyHeader from '#src/components/StickyHeader'
import ChatSidebar from '#src/components/ChatSidebar'
import isSupportedBrowser from '#src/services/unsupportedBrowsers'
import classnames from 'classnames'
import styles from './styles.css'

const Header: React.FC<HeaderProps> = ({
  className,
  stickyHeader,
  stickyHeaderAfterScroll,
  location,
  sidebar,
  sidebarConversation
}) => {
  const { myself } = useContext(IdentityContext)
  const userAgent = useContext(UserAgentContext)
  const departmentContext = useDepartmentContext()
  const history = useHistory()

  const isLargerThanSmall = useBreakpoint('sm')
  const isLargerThanLarge = useBreakpoint('lg')

  const [isVeilVisible, setIsVeilVisible] = useState(false)
  const [isSearchVisible, setIsSearchVisible] = useState(false)
  const [isMobileBrowseMenuVisible, setIsMobileBrowseMenuVisible] = useState(false)
  const [isMobileAccountMenuVisible, setIsMobileAccountMenuVisible] = useState(false)

  const hasOpenPanel =
    location.hash === '#cart' ||
    isVeilVisible ||
    isSearchVisible ||
    isMobileAccountMenuVisible ||
    isMobileBrowseMenuVisible

  const [activeDepartmentSlug, setActiveDepartmentSlug] = useState<string | undefined | null>(null)
  const [categorySlugConstraint, setCategorySlugConstraint] = useState<string | null>(null)
  const [
    activeCategory,
    setActiveCategory
  ] = useState<DepartmentNavigationTopLevelItemFragment | null>(null)
  const [
    activeSubSection,
    setActiveSubSection
  ] = useState<DepartmentNavigationSubSectionFragment | null>(null)
  const [activeSubSections, setActiveSubSections] = useState<
    DepartmentNavigationSubSectionFragment[]
  >([])
  const [activeTertiaryItems, setActiveTertiaryItems] = useState<
    DepartmentNavigationTertiaryItemFragment[]
  >([])

  /**
   * If the department updates, wipe all stored values, the component will remount
   * in the event you completely hover off so this handles on menu navigation.
   */
  useEffect(() => {
    setActiveCategory(null)
    setActiveSubSection(null)
    setActiveSubSections([])
    setActiveTertiaryItems([])
  }, [activeDepartmentSlug])

  // If the category or sub-section updates, wipe the tertiary navigation
  useEffect(() => {
    setActiveTertiaryItems([])
    setActiveSubSection(null)
  }, [activeCategory])

  return (
    <NavigationContextProvider
      value={{
        isMobileAccountMenuVisible,
        onToggleIsMobileAccountMenuVisible: (isVisible: boolean) =>
          setIsMobileAccountMenuVisible(isVisible),
        onToggleIsMobileBrowseMenuVisible: (isVisible: boolean) => {
          setIsMobileBrowseMenuVisible(isVisible)

          // We need some global state so the header can interact with the chat widget
          history.replace({
            ...location,
            state: {
              ...location.state,
              isBrowseExpanded: isVisible
            }
          })
        },
        isMobileBrowseMenuVisible,
        onToggleIsVeilVisible: (isVisible: boolean) => setIsVeilVisible(isVisible),
        isVeilVisible,
        onToggleIsSearchVisible: (isVisible: boolean) => {
          setIsVeilVisible(isVisible)
          setIsSearchVisible(isVisible)

          // We need some global state so the header can interact with the chat widget
          history.replace({
            ...location,
            state: {
              ...location.state,
              isSearchExpanded: isVisible
            }
          })
        },
        isSearchVisible,
        onSetActiveDepartmentSlug: departmentSlug => setActiveDepartmentSlug(departmentSlug),
        activeDepartmentSlug,
        onSetCategorySlugConstraint: categorySlug => setCategorySlugConstraint(categorySlug),
        categorySlugConstraint,
        onSetActiveCategory: category => setActiveCategory(category),
        activeCategory,
        onSetActiveSubSections: subSections => setActiveSubSections(subSections),
        activeSubSections,
        onSetActiveSubSection: subSection => setActiveSubSection(subSection),
        activeSubSection,
        onSetActiveTertiaryItems: tertiaryItems => setActiveTertiaryItems(tertiaryItems),
        activeTertiaryItems,
        onNavItemSelected: () => {
          /**
           * Reset all the 'selected' items and call the prop, there isn't an expectation that
           * the menu is drilled down when you expand it again. Resetting the department ID
           * will wipe the balance of the selected items.
           **/
          setActiveDepartmentSlug(null)
          setIsMobileAccountMenuVisible(false)
          setIsVeilVisible(false)
        }
      }}
    >
      {((stickyHeader && !stickyHeaderAfterScroll) || stickyHeaderAfterScroll) && (
        <StickyHeader>{stickyHeader}</StickyHeader>
      )}
      <div
        className={classnames(styles.wrapper, className, {
          [styles.wrapperLifted]: hasOpenPanel
        })}
      >
        <header
          className={classnames(styles.header, {
            [styles.unsupportedHeader]: !isSupportedBrowser(userAgent),
            [styles.isImpersonating]: myself?.realUser.id !== myself?.user.id
          })}
        >
          <nav className={styles.navigation}>
            {!isLargerThanSmall && (
              <div className={styles.mobileNavigation}>
                <Mobile />
              </div>
            )}
            {isLargerThanSmall && !isLargerThanLarge && (
              <div className={styles.tabletNavigation}>
                <Tablet />
              </div>
            )}
            {/**
             * The desktop nav must be rendered even on smaller viewports, for crawlers, in order
             *   for the site to be fully crawlable. In that case, the markup is rendered but
             *   the nav is visually hidden.
             */}
            {(isLargerThanLarge || userAgent?.isBot) && (
              <div className={styles.desktopNavigation}>
                <Desktop />
              </div>
            )}
          </nav>
          <NavigationVeil />
        </header>

        {departmentContext.department && <Search department={departmentContext.department} />}

        <Cart />

        {sidebar && <ChatSidebar className={styles.sidebar} conversation={sidebarConversation} />}
      </div>
    </NavigationContextProvider>
  )
}

export default Header
